import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Clock, CheckCircle, XCircle, Loader, AlertTriangle, DollarSign, Cpu, Shield, Star, X, Lock, Unlock, ShieldCheck } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { reports as reportsApi } from '../utils/api';
import Logo from '../components/Logo';
import { useAuth } from '../hooks/useAuth';

// Configure marked for professional report rendering
marked.setOptions({ gfm: true, breaks: true });

// Format duration from milliseconds to human-readable
const formatDuration = (ms) => {
  if (!ms) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
};

const ReportView = () => {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewHover, setReviewHover] = useState(0);
  const [adminAccessWorking, setAdminAccessWorking] = useState(false);
  const [adminAccessMsg, setAdminAccessMsg] = useState('');

  useEffect(() => {
    loadReport();
    const interval = setInterval(() => {
      if (report?.status === 'processing' || report?.status === 'pending') {
        loadReport();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [id, report?.status]);

  const loadReport = async () => {
    try {
      const data = await reportsApi.get(id);
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const blob = await reportsApi.download(id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DD-Report-${report.companyName.replace(/[^a-zA-Z0-9]/g, '-')}.${format}`;
      a.click();
      // Show review popup once per report
      const reviewKey = `reviewed_${id}`;
      if (!localStorage.getItem(reviewKey)) {
        setTimeout(() => setShowReviewModal(true), 1500);
      }
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  const handleAdminAccess = async (action) => {
    setAdminAccessWorking(true);
    setAdminAccessMsg('');
    try {
      const result = await reportsApi.updateAdminAccess(id, action);
      setReport(prev => ({ ...prev, adminAccess: result.adminAccess }));
      setAdminAccessMsg(action === 'grant'
        ? 'Access granted. Administrators can view this report for 24 hours.'
        : 'Access revoked. Administrators can no longer view this report content.');
    } catch (err) {
      setAdminAccessMsg('Error: ' + err.message);
    } finally {
      setAdminAccessWorking(false);
    }
  };

  const handleSubmitReview = async () => {
    if (reviewRating < 1) return;
    setReviewSubmitting(true);
    try {
      await reportsApi.submitReview(id, { rating: reviewRating, reviewText });
      localStorage.setItem(`reviewed_${id}`, 'true');
      setShowReviewModal(false);
    } catch (err) {
      console.error('Review submit failed:', err.message);
      localStorage.setItem(`reviewed_${id}`, 'true');
      setShowReviewModal(false);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleCloseReview = () => {
    localStorage.setItem(`reviewed_${id}`, 'true');
    setShowReviewModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Error Loading Report</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/reports" className="text-red-600 hover:text-red-700">← Back to Reports</Link>
        </div>
      </div>
    );
  }

  const riskColors = {
    'low': 'bg-green-500', 'low-moderate': 'bg-teal-500', 'moderate': 'bg-yellow-500',
    'moderate-high': 'bg-orange-500', 'high': 'bg-red-500', 'extreme': 'bg-red-700'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/reports" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
            Back to Reports
          </Link>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin-portal-7x9k2m/dashboard" className="flex items-center gap-1 text-red-700 hover:text-red-900 text-sm font-medium">
                <Shield className="h-4 w-4" />
                Admin
              </Link>
            )}
            <Logo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Header Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">{report?.companyName}</h1>
              <p className="text-gray-500 font-mono text-sm mt-1">{report?.reportId}</p>
            </div>
            <div className="flex items-center gap-4">
              {report?.status === 'completed' && (
                <>
                  <span className={`px-4 py-2 rounded-full text-white font-semibold ${riskColors[report.riskLevel] || 'bg-gray-500'}`}>
                    {report.riskLevel?.toUpperCase()} RISK
                  </span>
                  <button onClick={() => handleDownload('docx')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
                    <Download className="h-4 w-4" />
                    Word
                  </button>
                  <button onClick={() => handleDownload('pdf')} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                    <Download className="h-4 w-4" />
                    PDF
                  </button>
                </>
              )}
              {(report?.status === 'pending' || report?.status === 'processing') && (
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader className="h-5 w-5 animate-spin" />
                  <span>Report is being generated...</span>
                </div>
              )}
              {report?.status === 'failed' && (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  <span>Report generation failed</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> This report is provided for information only and is based on publicly available open-source information which may or may not be accurate, complete, or current. It does not constitute legal advice.
            </p>
          </div>
        </div>

        {/* Admin Access Control — shown only to the report owner (non-admin) */}
        {report?.status === 'completed' && user?.role !== 'admin' && (() => {
          const access = report.adminAccess;
          const isActive = access?.granted && access?.expiresAt && new Date(access.expiresAt) > new Date();
          const expiresIn = isActive
            ? (() => {
                const ms = new Date(access.expiresAt) - new Date();
                const h = Math.floor(ms / 3600000);
                const m = Math.floor((ms % 3600000) / 60000);
                return h > 0 ? `${h}h ${m}m` : `${m}m`;
              })()
            : null;

          return (
            <div className={`border rounded-lg p-5 mb-6 ${isActive ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 flex-shrink-0 ${isActive ? 'text-amber-500' : 'text-gray-400'}`}>
                  {isActive ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold text-sm mb-1 ${isActive ? 'text-amber-800' : 'text-gray-700'}`}>
                    {isActive ? `Administrator access granted — expires in ${expiresIn}` : 'Report content is private'}
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    {isActive
                      ? 'An administrator can currently view the full content of this report. You can revoke this access at any time.'
                      : 'Only you can view this report. If you need to share it with an administrator for support purposes, you can grant temporary 24-hour access below.'}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {isActive ? (
                      <button
                        onClick={() => handleAdminAccess('revoke')}
                        disabled={adminAccessWorking}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                      >
                        <Lock className="h-4 w-4" />
                        {adminAccessWorking ? 'Revoking…' : 'Revoke administrator access'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAdminAccess('grant')}
                        disabled={adminAccessWorking}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-md disabled:opacity-50"
                      >
                        <ShieldCheck className="h-4 w-4" />
                        {adminAccessWorking ? 'Granting…' : 'Grant administrator access for 24 hours'}
                      </button>
                    )}
                    {adminAccessMsg && (
                      <p className={`text-xs ${adminAccessMsg.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}>
                        {adminAccessMsg}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Cost & Metadata (Admin/Richard only) */}
        {report?.status === 'completed' && report?.showCostData && (user?.role === 'admin' || user?.email === 'richard@id100.com') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-900">Report Cost & Metadata (Admin Only)</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">AI Model Used</p>
                <p className="font-semibold text-sm">{report.aiModel || 'N/A'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Report Cost</p>
                <p className="font-semibold text-sm text-green-600">${report.reportCost?.toFixed(4) || '0.0000'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Input Tokens</p>
                <p className="font-semibold text-sm">{report.inputTokens?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Output Tokens</p>
                <p className="font-semibold text-sm">{report.outputTokens?.toLocaleString() || '0'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Search Count</p>
                <p className="font-semibold text-sm">{report.searchCount || 0} searches</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Word Count</p>
                <p className="font-semibold text-sm">{report.wordCount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Quality Score</p>
                <p className="font-semibold text-sm">{report.qualityScore ? `${report.qualityScore}/10` : 'N/A'}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Processing Time</p>
                <p className="font-semibold text-sm">{report.processingTime ? `${Math.round(report.processingTime/1000)}s` : 'N/A'}</p>
              </div>
            </div>

            {/* Per-Agent Cost Breakdown */}
            {report.agentMetadata && report.costBreakdown && (() => {
              const AGENT_ROWS = [
                { key: 'webResearch',                  label: 'Web Research (1)' },
                { key: 'deepResearch',                 label: 'Deep Research (2a)' },
                { key: 'advancedResearch',             label: 'Advanced Research (2b)' },
                { key: 'supplementaryResearch',        label: 'Supplementary Research (2c)' },
                { key: 'secondarySupplementaryResearch', label: 'Secondary Supplementary (2d)' },
                { key: 'arranger',                     label: 'Arranger (3)' },
                { key: 'checker',                      label: 'Checker (4)' },
                { key: 'orchestrator',                 label: 'Orchestrator (5)' },
              ];

              // Infer provider from metadata or model id prefix
              const inferProvider = (meta) => {
                if (!meta) return 'other';
                if (meta.provider) return meta.provider;
                const m = (meta.model || '').toLowerCase();
                if (m.startsWith('claude-')) return 'anthropic';
                if (m.startsWith('gpt-') || m.startsWith('o3') || m.startsWith('o4-')) return 'openai';
                if (m.startsWith('gemini-')) return 'google';
                if (m.startsWith('grok-')) return 'xai';
                if (m.includes('/')) return 'openrouter';
                return 'other';
              };

              // Compute cost-by-provider totals
              const providerTotals = {};
              AGENT_ROWS.forEach(a => {
                const meta = report.agentMetadata[a.key];
                if (!meta || meta.model === 'failed') return;
                const cost = report.costBreakdown[a.key];
                if (cost == null) return;
                const p = inferProvider(meta);
                providerTotals[p] = (providerTotals[p] || 0) + cost;
              });

              const providerLabels = {
                anthropic: 'Anthropic', openai: 'OpenAI', google: 'Google',
                xai: 'xAI', openrouter: 'OpenRouter', other: 'Other'
              };

              const fmtRate = (meta) => {
                if (meta?.inputPrice != null && meta?.outputPrice != null) {
                  return `$${Number(meta.inputPrice).toFixed(2)}/$${Number(meta.outputPrice).toFixed(2)}`;
                }
                return '—';
              };

              return (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-1">
                    <Cpu className="h-4 w-4" />
                    Agent Cost Breakdown
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-100 text-blue-800">
                          <th className="text-left px-3 py-2 rounded-tl-md">Agent</th>
                          <th className="text-left px-3 py-2">Model</th>
                          <th className="text-right px-3 py-2" title="Input / Output price per million tokens">Rate $/MTok</th>
                          <th className="text-right px-3 py-2">Input Tokens</th>
                          <th className="text-right px-3 py-2">Output Tokens</th>
                          <th className="text-right px-3 py-2">Searches</th>
                          <th className="text-right px-3 py-2">Duration</th>
                          <th className="text-right px-3 py-2 rounded-tr-md">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {AGENT_ROWS.map(agent => {
                          const meta = report.agentMetadata[agent.key];
                          if (!meta || meta.model === 'failed') return null;
                          const cost = report.costBreakdown[agent.key];
                          const hasInputTokens = meta.inputTokens != null;
                          const hasOutputTokens = meta.outputTokens != null;
                          // Fallback info — fallbackIndex 0 means primary ran
                          const fallbackIndex = meta.fallbackIndex ?? 0;
                          const usedFallback = meta.usedFallback === true || fallbackIndex > 0;
                          const configuredModel = meta.configuredModel;
                          const failedAttempts = Array.isArray(meta.failedAttempts) ? meta.failedAttempts : [];
                          return (
                            <React.Fragment key={agent.key}>
                              <tr className="border-b border-blue-50 hover:bg-blue-50/50">
                                <td className="px-3 py-1.5 font-medium text-gray-700">{agent.label}</td>
                                <td className="px-3 py-1.5 text-gray-500">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span>{meta.model}</span>
                                    {meta.zdr === true && (
                                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 text-purple-700 border border-purple-300" title="Zero Data Retention">ZDR</span>
                                    )}
                                    {usedFallback && (
                                      <span
                                        className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-300"
                                        title={`Primary model "${configuredModel || 'unknown'}" failed — recovered using fallback #${fallbackIndex}`}
                                      >
                                        Fallback #{fallbackIndex}
                                      </span>
                                    )}
                                  </div>
                                  {usedFallback && configuredModel && configuredModel !== meta.model && (
                                    <div className="text-[10px] text-amber-700 mt-0.5">
                                      configured: <span className="line-through">{configuredModel}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right text-gray-500 font-mono">{fmtRate(meta)}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{hasInputTokens ? meta.inputTokens.toLocaleString() : '—'}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{hasOutputTokens ? meta.outputTokens.toLocaleString() : '—'}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{meta.searchCount != null ? meta.searchCount : '—'}</td>
                                <td className="px-3 py-1.5 text-right text-gray-600">{formatDuration(meta.duration)}</td>
                                <td className="px-3 py-1.5 text-right font-medium text-green-600">{cost != null ? `$${cost.toFixed(4)}` : '—'}</td>
                              </tr>
                              {/* Failed attempts sub-rows — shown when fallback was used */}
                              {failedAttempts.length > 0 && failedAttempts.map((fa, idx) => (
                                <tr key={`${agent.key}-fail-${idx}`} className="border-b border-red-100 bg-red-50/30 text-[11px]">
                                  <td className="px-3 py-1 pl-6 text-red-700">↳ failed attempt #{fa.fallbackIndex ?? idx}</td>
                                  <td className="px-3 py-1 text-red-700" colSpan="7">
                                    <span className="font-mono">{fa.provider}/{fa.model}</span>
                                    {fa.error && <span className="ml-2 text-red-600 italic">{fa.error.length > 120 ? fa.error.slice(0, 120) + '…' : fa.error}</span>}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        }).filter(Boolean)}

                        {/* Retry row — shown when a retry cycle ran */}
                        {(() => {
                          const retryMeta = report.agentMetadata.retry;
                          const retryPerformed = report.agentMetadata.retryPerformed;
                          if (!retryPerformed) return null;

                          if (retryMeta) {
                            const retryCost = retryMeta.cost;
                            const cbd = retryMeta.costBreakdown;
                            return (
                              <>
                                <tr key="retry" className="border-b border-blue-50 hover:bg-blue-50/50 bg-amber-50/40">
                                  <td className="px-3 py-1.5 font-medium text-amber-700">
                                    Retry Cycle
                                    {retryMeta.totalAttempts > 1 && <span className="ml-1 text-xs text-amber-600">×{retryMeta.totalAttempts}</span>}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-500 italic text-xs">{retryMeta.model || 'multi-agent'}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-400 font-mono text-xs">—</td>
                                  <td className="px-3 py-1.5 text-right text-gray-600">{retryMeta.inputTokens != null ? retryMeta.inputTokens.toLocaleString() : '—'}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-600">{retryMeta.outputTokens != null ? retryMeta.outputTokens.toLocaleString() : '—'}</td>
                                  <td className="px-3 py-1.5 text-right text-amber-700 font-medium">{retryMeta.searchCount != null ? retryMeta.searchCount : '—'}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-600">—</td>
                                  <td className="px-3 py-1.5 text-right font-medium text-green-600">
                                    {retryCost != null ? `$${retryCost.toFixed(4)}` : '—'}
                                  </td>
                                </tr>
                                {/* Show per-sub-agent cost breakdown if available */}
                                {cbd && (
                                  <tr key="retry-breakdown" className="border-b border-amber-100 bg-amber-50/20 text-[11px]">
                                    <td className="px-3 py-1 pl-6 text-amber-600 italic" colSpan="7">
                                      ↳ retry research ${(cbd.research||0).toFixed(4)} · re-arrange ${(cbd.arrange||0).toFixed(4)} · re-check ${(cbd.check||0).toFixed(4)}
                                    </td>
                                    <td></td>
                                  </tr>
                                )}
                              </>
                            );
                          }

                          // Retry ran but metadata wasn't saved (older report)
                          const agentSearchTotal = ['webResearch','deepResearch','advancedResearch','supplementaryResearch'].reduce((sum, key) => {
                            return sum + (report.agentMetadata[key]?.searchCount || 0);
                          }, 0);
                          const unaccountedSearches = (report.searchCount || 0) - agentSearchTotal;
                          if (unaccountedSearches <= 0) return null;
                          return (
                            <tr key="retry" className="border-b border-blue-50 hover:bg-blue-50/50 bg-amber-50/40">
                              <td className="px-3 py-1.5 font-medium text-amber-700">Retry Cycle †</td>
                              <td className="px-3 py-1.5 text-gray-400 italic text-xs">supplementary research</td>
                              <td className="px-3 py-1.5 text-right text-gray-400">—</td>
                              <td className="px-3 py-1.5 text-right text-gray-400">—</td>
                              <td className="px-3 py-1.5 text-right text-gray-400">—</td>
                              <td className="px-3 py-1.5 text-right text-amber-700 font-medium">{unaccountedSearches}</td>
                              <td className="px-3 py-1.5 text-right text-gray-400">—</td>
                              <td className="px-3 py-1.5 text-right text-gray-400">—</td>
                            </tr>
                          );
                        })()}

                        {(() => {
                          // ── VAT + GBP totals ─────────────────────────────
                          // Backend returns costSummary on admin requests. Fall
                          // back to recomputing locally if the field is missing
                          // (for older reports or frontend hot-reload state).
                          const summary = report.costSummary || {};
                          const vatRate = typeof summary.vatRate === 'number' ? summary.vatRate : (typeof report.vatRate === 'number' ? report.vatRate : 0.20);
                          const usdExVat = summary.usdExVat ?? (report.reportCost || 0);
                          const usdIncVat = summary.usdIncVat ?? (usdExVat * (1 + vatRate));
                          const gbpRate = summary.gbpUsdRate ?? (report.gbpUsdRate ?? null);
                          const gbpRateDate = summary.gbpUsdRateDate ?? (report.gbpUsdRateDate ?? null);
                          const gbpIncVat = summary.gbpIncVat ?? (gbpRate ? (usdIncVat / gbpRate) : null);
                          return (
                            <>
                              <tr className="bg-blue-50">
                                <td className="px-3 pt-2 pb-1 font-semibold text-gray-700 rounded-bl-md" colSpan="5">Total (USD, ex VAT)</td>
                                <td className="px-3 pt-2 pb-1 text-right text-blue-800 font-semibold">{report.searchCount || 0} searches</td>
                                <td className="px-3 pt-2 pb-1 text-right text-gray-600">—</td>
                                <td className="px-3 pt-2 pb-1 text-right text-green-700 font-semibold">${usdExVat.toFixed(4)}</td>
                              </tr>
                              <tr className="bg-blue-50">
                                <td className="px-3 py-1 font-medium text-gray-600 text-xs" colSpan="7">+ UK VAT @ {(vatRate * 100).toFixed(0)}%</td>
                                <td className="px-3 py-1 text-right text-gray-600 text-xs">${(usdIncVat - usdExVat).toFixed(4)}</td>
                              </tr>
                              <tr className="bg-blue-50">
                                <td className="px-3 py-1 font-semibold text-gray-700" colSpan="7">Total (USD, inc VAT)</td>
                                <td className="px-3 py-1 text-right text-green-700 font-semibold">${usdIncVat.toFixed(4)}</td>
                              </tr>
                              <tr className="bg-blue-100">
                                <td className="px-3 py-2 font-semibold text-gray-800 rounded-bl-md" colSpan="7">
                                  Total (GBP, inc VAT)
                                  {gbpRate && gbpRateDate ? (
                                    <span className="ml-2 text-[10px] text-gray-500 font-normal">
                                      (mid-rate £1 = ${gbpRate.toFixed(4)} on {gbpRateDate})
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-[10px] text-amber-600 font-normal">
                                      (GBP rate unavailable)
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-green-800 rounded-br-md">
                                  {gbpIncVat !== null ? `£${gbpIncVat.toFixed(4)}` : '—'}
                                </td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                    {report.agentMetadata?.retryPerformed && !report.agentMetadata?.retry && (
                      <p className="text-xs text-amber-600 mt-1.5">† Retry cycle detail not available for this report — search count included in total.</p>
                    )}
                  </div>

                  {/* Cost by Provider summary */}
                  {Object.keys(providerTotals).length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">Cost by Provider</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(providerTotals)
                          .sort((a, b) => b[1] - a[1])
                          .map(([provider, total]) => (
                            <div key={provider} className="px-3 py-2 bg-white border border-blue-200 rounded-md flex items-center gap-2 text-xs">
                              <span className="font-medium text-gray-700">{providerLabels[provider] || provider}</span>
                              <span className="font-mono font-semibold text-green-700">${total.toFixed(4)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Report Content */}
        {report?.status === 'completed' && report?.content && (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="report-content prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(report.content)) }} />
          </div>
        )}

        {report?.status === 'processing' && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Loader className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold mb-2">Generating Your Report</h2>
            <p className="text-gray-600">This usually takes 30-60 minutes. You'll receive an email when it's ready.</p>
          </div>
        )}

        {report?.status === 'failed' && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Report Generation Failed</h2>
            <p className="text-gray-600 mb-4">{report.errorMessage || 'An error occurred while generating your report. Your credit has been refunded.'}</p>
            <Link to="/reports/new" className="bg-red-600 text-white px-6 py-2 rounded-md inline-block">
              Try Again
            </Link>
          </div>
        )}
      </main>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">How was this report?</h3>
              <button onClick={handleCloseReview} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-4">Your feedback helps us improve. This is optional.</p>

            <div className="flex gap-1 mb-4 justify-center">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setReviewRating(star)}
                  onMouseEnter={() => setReviewHover(star)}
                  onMouseLeave={() => setReviewHover(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star className={`h-8 w-8 ${
                    star <= (reviewHover || reviewRating)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-600'
                  }`} />
                </button>
              ))}
            </div>

            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Tell us what you think... (optional)"
              rows={3}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
            />

            <div className="flex gap-3">
              <button
                onClick={handleSubmitReview}
                disabled={reviewRating < 1 || reviewSubmitting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button
                onClick={handleCloseReview}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 text-sm"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView;
